import React, { useState, useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { TextInput, SearchBar, Theme } from '@simplybenefits/sb-ui-library';
import loadingGif from '../../../../assets/loading.gif';

/**
 * Sets alternating row colours
 * @param {*} index 
 * @returns 
 */
const getRowBackgroundColor = (index) => index % 2 === 0 ? "#FFFFFF" : Theme.colors.backgroundGrey;

/**
 * 
 * @param {*} data
 * @param {*} handleScroll 
 * @param {*} currentYearFilter 
 * @param {*} currentSearchTerm 
 * @param {*} search 
 * @param {*} handleInput 
 * @param {*} searching 
 * @returns 
 */
const CodesTable = ({ data, handleScroll, currentYearFilter, currentSearchTerm, search, setYear, searching, codeTypeFilter, setCodeType }) => {
    const codeTypes = [ 'All', 'General (GP)', 'Denturist (DT)', 'Hygienist (RDH)', 'Specialist (SP)', 'Quebec (ACDQ/QC)' ];
    const [ years, setYears ] = useState([]);

    useEffect(() => {
        // Create Array starting in 2022 to current year for filtering purposes
        const startYear = 2022;
        const currentYear = new Date().getFullYear();
        const yearsArray = [ 'All', ...Array.from({ length: currentYear - startYear + 1 }, (_, index) => (startYear + index).toString()) ];
        setYears(yearsArray);
    }, []);

    const styles = {
        tableContainer: {
            maxHeight: '60vh', 
            border: `1px solid ${ Theme.colors.lightGrey }`, 
            overflowY: 'auto', 
            borderRadius: '10px'
        },
        codesHeaderRow: {
            fontSize: '8px',
            fontFamily: Theme.fonts.type.heading,
            border: `1px solid ${ Theme.colors.lightGrey }`,
            backgroundColor: Theme.colors.backgroundGrey,
            boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.16)',
            padding: '5px',
            lineHeight: '1.25',
            height: '40px'
        },
        codesRow: {
            fontSize: '10px',
            fontFamily: Theme.fonts.type.medium,
            border: `1px solid ${ Theme.colors.lightGrey }`,
            padding: '5px'
        },
        alignCell: {
            textAlign: 'center'
        },
        noWrapCell: {
            whiteSpace: 'nowrap'
        },
        searchWrapper: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            margin: "25px 0px 30px 0px"
        },
        searchBar: {
            flex: 1
        },
        filterBlock: {
            width: "25%",
            marginLeft: "25px"
        },
        hoverRow: {
            '&:hover': {
                backgroundColor: Theme.colors.lightBlue
            } 
        },
        loaderContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '60vh',
            borderRadius: '10px',
            backgroundColor: 'white'
        }
    };

    return (
        
        <>
            <div style={styles.searchWrapper}>
                <SearchBar
                    value={currentSearchTerm}
                    search={(e) => search(e)}
                    customStyle={styles.searchBar}
                /> 
                <TextInput
                    label="Filter By Year"
                    variant="outlined"
                    margin="none"
                    value={ currentYearFilter }
                    onChange={ (value) => setYear(value) }
                    select
                    customStyle={styles.filterBlock}
                    selectOptions={ years }
                />
                <TextInput
                    label="Filter By Code Type"
                    variant="outlined"
                    margin="none"
                    value={ codeTypeFilter }
                    onChange={ (value) => setCodeType(value) }
                    select
                    customStyle={styles.filterBlock}
                    selectOptions={ codeTypes }
                />
            </div>

            {
                searching ?
                    <div style={styles.loaderContainer}>
                        <img src={ loadingGif } alt="loading..." />
                    </div>
                    :
                    <TableContainer onScroll={handleScroll} style={styles.tableContainer}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell style={styles.codesHeaderRow}>Code</TableCell>
                                    <TableCell style={{ ...styles.codesHeaderRow, ...styles.alignCell }}>Code Type</TableCell>
                                    <TableCell style={{ ...styles.codesHeaderRow, ...styles.alignCell }}>Claim<br/>Type</TableCell>
                                    <TableCell style={{ ...styles.codesHeaderRow, ...styles.alignCell }}>Year</TableCell>
                                    <TableCell style={ styles.codesHeaderRow }>English Description</TableCell>
                                    <TableCell style={ styles.codesHeaderRow }>French Description</TableCell>
                                    <TableCell style={{ ...styles.codesHeaderRow, ...styles.alignCell }}>Procedure<br/>Flag</TableCell>
                                    <TableCell style={{ ...styles.codesHeaderRow, ...styles.alignCell }}>BC</TableCell>
                                    <TableCell style={{ ...styles.codesHeaderRow, ...styles.alignCell }}>AB</TableCell>
                                    <TableCell style={{ ...styles.codesHeaderRow, ...styles.alignCell }}>MB</TableCell>
                                    <TableCell style={{ ...styles.codesHeaderRow, ...styles.alignCell }}>SK</TableCell>
                                    <TableCell style={{ ...styles.codesHeaderRow, ...styles.alignCell }}>ON</TableCell>
                                    <TableCell style={{ ...styles.codesHeaderRow, ...styles.alignCell }}>NB</TableCell>
                                    <TableCell style={{ ...styles.codesHeaderRow, ...styles.alignCell }}>NL</TableCell>
                                    <TableCell style={{ ...styles.codesHeaderRow, ...styles.alignCell }}>NS</TableCell>
                                    <TableCell style={{ ...styles.codesHeaderRow, ...styles.alignCell }}>PE</TableCell>
                                    <TableCell style={{ ...styles.codesHeaderRow, ...styles.alignCell }}>NT</TableCell>
                                    <TableCell style={{ ...styles.codesHeaderRow, ...styles.alignCell }}>NU</TableCell>
                                    <TableCell style={{ ...styles.codesHeaderRow, ...styles.alignCell }}>QC</TableCell>
                                    <TableCell style={{ ...styles.codesHeaderRow, ...styles.alignCell }}>YK</TableCell>
                                    <TableCell style={{ ...styles.codesHeaderRow, ...styles.alignCell }}>Age Limit</TableCell>
                                    <TableCell style={{ ...styles.codesHeaderRow, ...styles.alignCell }}>Months<br/>to Recall</TableCell>
                                    <TableCell style={{ ...styles.codesHeaderRow, ...styles.alignCell }}>Uses<br/>Calendar Year</TableCell>
                                    <TableCell style={{ ...styles.codesHeaderRow, ...styles.alignCell }}>Is<br/>Lifetime</TableCell>
                                    <TableCell style={{ ...styles.codesHeaderRow, ...styles.alignCell }}>Is Plan<br/>Design Recall</TableCell>
                                    <TableCell style={{ ...styles.codesHeaderRow, ...styles.alignCell }}>Grouped with Codes</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {
                                    data.map((code, index) => (
                                        <TableRow key={index} sx={{ backgroundColor: getRowBackgroundColor(index), ...styles.hoverRow }}>
                                            <TableCell style={styles.codesRow}>{code.code}</TableCell>
                                            <TableCell style={{ ...styles.codesRow, ...styles.alignCell }}>{code.codeType}</TableCell>
                                            <TableCell style={{ ...styles.codesRow, ...styles.alignCell }}>{code.claimType}</TableCell>
                                            <TableCell style={{ ...styles.codesRow, ...styles.alignCell }}>{code.year}</TableCell>
                                            <TableCell style={{ ...styles.codesRow, width: '15%' }}>{code.enDesc}</TableCell>
                                            <TableCell style={{ ...styles.codesRow, width: '15%' }}>{code.frDesc}</TableCell>
                                            <TableCell style={{ ...styles.codesRow, ...styles.alignCell }}>{code.isFlagged}</TableCell>
                                            <TableCell style={{ ...styles.codesRow, ...styles.alignCell }}>{code.feeBC}</TableCell>
                                            <TableCell style={{ ...styles.codesRow, ...styles.alignCell }}>{code.feeAB}</TableCell>
                                            <TableCell style={{ ...styles.codesRow, ...styles.alignCell }}>{code.feeMB}</TableCell>
                                            <TableCell style={{ ...styles.codesRow, ...styles.alignCell }}>{code.feeSK}</TableCell>
                                            <TableCell style={{ ...styles.codesRow, ...styles.alignCell }}>{code.feeON}</TableCell>
                                            <TableCell style={{ ...styles.codesRow, ...styles.alignCell }}>{code.feeNB}</TableCell>
                                            <TableCell style={{ ...styles.codesRow, ...styles.alignCell }}>{code.feeNL}</TableCell>
                                            <TableCell style={{ ...styles.codesRow, ...styles.alignCell }}>{code.feeNS}</TableCell>
                                            <TableCell style={{ ...styles.codesRow, ...styles.alignCell }}>{code.feePE}</TableCell>
                                            <TableCell style={{ ...styles.codesRow, ...styles.alignCell }}>{code.feeNT}</TableCell>
                                            <TableCell style={{ ...styles.codesRow, ...styles.alignCell }}>{code.feeNU}</TableCell>
                                            <TableCell style={{ ...styles.codesRow, ...styles.alignCell }}>{code.feeQC}</TableCell>
                                            <TableCell style={{ ...styles.codesRow, ...styles.alignCell }}>{code.feeYK}</TableCell>
                                            <TableCell style={{ ...styles.codesRow, ...styles.alignCell }}>{code.ageLimit}</TableCell>
                                            <TableCell style={{ ...styles.codesRow, ...styles.alignCell }}>{code.monthsToRecall}</TableCell>
                                            <TableCell style={{ ...styles.codesRow, ...styles.alignCell }}>{code.isCalendarYear}</TableCell>
                                            <TableCell style={{ ...styles.codesRow, ...styles.alignCell }}>{code.isLifetime}</TableCell>
                                            <TableCell style={{ ...styles.codesRow, ...styles.alignCell }}>{code.isPlanDesignRecall}</TableCell>
                                            <TableCell sx={{ ...styles.codesRow, width: '15%' }}>{code.groupedWith}</TableCell>
                                        </TableRow>
                                    ))
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>  
            }
        </>
    );
};

export default CodesTable;